<script setup lang="ts">
// import { ref, onMounted, computed, watch, onUnmounted } from 'vue';
import type { Music } from '~/services/musicService';
import { useI18n } from 'vue-i18n';

const { t, locale } = useI18n();

// 播放器状态
const showPlayer = ref(false);
const isPlaying = ref(false);
const currentMusic = ref<any>(null);
const progress = ref(0);
const volume = ref(0.7);
const isMuted = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const playlist = ref<Music[]>([]);
const audioElement = ref<HTMLAudioElement | null>(null);

// 通过自定义事件通知父组件播放器状态
const notifyPlayerVisibility = () => {
  // 使用自定义事件通知父组件
  window.dispatchEvent(new CustomEvent('music-player-visibility-change', {
    detail: { isVisible: showPlayer.value }
  }));
};

// 响应状态请求事件
const handleStateRequest = () => {
  notifyPlayerVisibility();
};

// 监听showPlayer变化
watch(() => showPlayer.value, () => {
  notifyPlayerVisibility();
});

// 组件挂载时发送初始状态
onMounted(() => {
  notifyPlayerVisibility();
  
  // 添加状态请求事件监听
  window.addEventListener('request-music-player-state', handleStateRequest);
});

// 组件卸载时移除事件监听
onUnmounted(() => {
  window.removeEventListener('request-music-player-state', handleStateRequest);
});

// 计算属性
const formattedCurrentTime = computed(() => formatTime(currentTime.value));
const formattedDuration = computed(() => formatTime(duration.value));

// 格式化时间
const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// 播放/暂停
const togglePlay = () => {
  if (!audioElement.value || !currentMusic.value || !currentMusic.value.audio_url) {
    console.error('无法播放：无效的音乐对象或音频元素');
    return;
  }
  
  if (isPlaying.value) {
    // 暂停播放
    audioElement.value.pause();
    isPlaying.value = false;
  } else {
    // 确保音频源已设置
    if (!audioElement.value.src || audioElement.value.src !== currentMusic.value.audio_url) {
      audioElement.value.src = currentMusic.value.audio_url;
      audioElement.value.load();
      
      // 使用canplay事件确保音频准备好再播放
      const canPlayHandler = () => {
        audioElement.value?.play().then(() => {
          isPlaying.value = true;
        }).catch((err: Error) => {
          console.error('播放失败:', err);
          isPlaying.value = false;
        }).finally(() => {
          audioElement.value?.removeEventListener('canplay', canPlayHandler);
        });
      };
      audioElement.value.addEventListener('canplay', canPlayHandler, { once: true });
    } else {
      // 源已设置，直接播放
      audioElement.value.play().then(() => {
        isPlaying.value = true;
      }).catch((err: Error) => {
        console.error('播放失败:', err);
        isPlaying.value = false;
      });
    }
  }
  
  // 触发全局事件，通知其他组件播放状态变化
  const event = new CustomEvent('playing-state-changed', { 
    detail: { 
      id: currentMusic.value.id, 
      isPlaying: isPlaying.value 
    } 
  });
  window.dispatchEvent(event);
};

// 上一首
const playPrevious = () => {
  if (playlist.value.length <= 1) return;
  
  const currentIndex = playlist.value.findIndex((m: Music) => m.id === currentMusic.value?.id);
  if (currentIndex > 0) {
    playMusic(playlist.value[currentIndex - 1]);
  } else {
    // 循环播放，从最后一首开始
    playMusic(playlist.value[playlist.value.length - 1]);
  }
};

// 下一首
const playNext = () => {
  if (playlist.value.length <= 1) return;
  
  const currentIndex = playlist.value.findIndex((m: Music) => m.id === currentMusic.value?.id);
  if (currentIndex < playlist.value.length - 1) {
    playMusic(playlist.value[currentIndex + 1]);
  } else {
    // 循环播放，从第一首开始
    playMusic(playlist.value[0]);
  }
};

// 设置进度
const setProgress = (e: MouseEvent) => {
  if (!audioElement.value || !currentMusic.value) return;
  
  const progressBar = e.currentTarget as HTMLDivElement;
  const rect = progressBar.getBoundingClientRect();
  const percent = (e.clientX - rect.left) / rect.width;
  
  audioElement.value.currentTime = percent * duration.value;
};

// 设置音量
const setVolume = (e: MouseEvent) => {
  if (!audioElement.value) return;
  
  const volumeBar = e.currentTarget as HTMLDivElement;
  const rect = volumeBar.getBoundingClientRect();
  const percent = (e.clientX - rect.left) / rect.width;
  
  volume.value = Math.max(0, Math.min(1, percent));
  audioElement.value.volume = volume.value;
  
  if (volume.value > 0) {
    isMuted.value = false;
  }
};

// 静音/取消静音
const toggleMute = () => {
  if (!audioElement.value) return;
  
  isMuted.value = !isMuted.value;
  audioElement.value.muted = isMuted.value;
};

// 播放音乐
const playMusic = (music: Music) => {
  // 确保音乐对象有效
  if (!music || !music.id) {
    console.error('无效的音乐对象:', music);
    return;
  }

  // 如果已经在播放同一首歌，则不重复加载
  if (currentMusic.value && currentMusic.value.id === music.id) {
    // 如果暂停了，则继续播放
    if (!isPlaying.value && audioElement.value) {
      audioElement.value.play().catch(error => {
        console.error('继续播放失败:', error);
        isPlaying.value = false;
      });
      isPlaying.value = true;
      
      // 触发全局事件
      const event = new CustomEvent('playing-state-changed', { 
        detail: { id: music.id, isPlaying: true } 
      });
      window.dispatchEvent(event);
    }
    return;
  }

  // 先暂停当前播放的音乐（如果有）
  if (audioElement.value) {
    audioElement.value.pause();
    // 清除当前源，防止冲突
    audioElement.value.removeAttribute('src');
    audioElement.value.load();
  }

  currentMusic.value = music;
  isPlaying.value = false; // 先设为false，等加载完成再播放
  
  if(!showPlayer.value){
    showPlayer.value = true; // 显示播放器
  }
  
  // 如果播放列表中没有这首歌，添加到播放列表
  if (!playlist.value.some((m: Music) => m.id === music.id)) {
    playlist.value.push(music);
  }
  
  // 重置进度
  currentTime.value = 0;
  progress.value = 0;
  
  // 设置音频时长（预设值）
  duration.value = music.metadata?.duration || 180;
  
  // 在下一个事件循环中设置音频源并播放
  setTimeout(() => {
    if (audioElement.value) {
      // 设置音频源
      audioElement.value.src = music.audio_url;
      audioElement.value.volume = volume.value;
      
      // 预加载
      audioElement.value.load();
      
      // 在canplay事件中播放，确保音频已准备好
      const canPlayHandler = () => {
        audioElement.value?.play().then(() => {
          isPlaying.value = true;
          
          // 触发全局事件
          const event = new CustomEvent('playing-state-changed', { 
            detail: { id: music.id, isPlaying: true } 
          });
          window.dispatchEvent(event);
        }).catch((error: Error) => {
          console.error('播放失败:', error);
          isPlaying.value = false;
          
          // 触发全局事件
          const failEvent = new CustomEvent('playing-state-changed', { 
            detail: { id: music.id, isPlaying: false } 
          });
          window.dispatchEvent(failEvent);
        }).finally(() => {
          // 移除一次性事件监听器
          audioElement.value?.removeEventListener('canplay', canPlayHandler);
        });
      };
      
      audioElement.value.addEventListener('canplay', canPlayHandler, { once: true });
    }
  }, 0);
};

// 音频事件处理函数
const handleTimeUpdate = () => {
  if (audioElement.value) {
    currentTime.value = audioElement.value.currentTime;
    progress.value = (currentTime.value / duration.value) * 100;
  }
};
  
const handleLoadedMetadata = () => {
  if (audioElement.value) {
    duration.value = audioElement.value.duration || currentMusic.value?.metadata?.duration || 180;
  }
};
  
const handleEnded = () => {
  isPlaying.value = false;
  
  // 触发全局事件，通知其他组件播放状态变化
  if (currentMusic.value) {
    const event = new CustomEvent('playing-state-changed', { 
      detail: { 
        id: currentMusic.value.id, 
        isPlaying: false 
      } 
    });
    window.dispatchEvent(event);
  }
  
  // 自动播放下一首
  if (playlist.value.length > 1) {
    playNext();
  }
};
  
// 全局事件处理函数
const handlePlayMusic = (e: CustomEvent) => {
  const music = e.detail;
  // 确保音乐对象有效
  if (!music || !music.id || !music.audio_url) {
    console.error('收到无效的音乐对象:', music);
    return;
  }
  // console.log('收到播放请求:', music);
  playMusic(music);
};
  
const handleTogglePlayState = (e: CustomEvent) => {
  const { isPlaying: newIsPlaying } = e.detail;
  
  // 如果没有当前音乐，则忽略请求
  if (!currentMusic.value || !audioElement.value) {
    return;
  }
  
  // 更新播放状态
  if (isPlaying.value !== newIsPlaying) {
    if (newIsPlaying) {
      // 请求播放
      // 确保音频源已设置
      if (!audioElement.value.src || audioElement.value.src !== currentMusic.value.audio_url) {
        audioElement.value.src = currentMusic.value.audio_url;
        audioElement.value.load();
        
        // 使用canplay事件确保音频准备好再播放
        const canPlayHandler = () => {
          audioElement.value?.play().then(() => {
            isPlaying.value = true;
          }).catch((err: Error) => {
            console.error('播放失败:', err);
            isPlaying.value = false;
            
            // 触发全局事件
            if (currentMusic.value) {
              const event = new CustomEvent('playing-state-changed', { 
                detail: { 
                  id: currentMusic.value.id, 
                  isPlaying: false 
                } 
              });
              window.dispatchEvent(event);
            }
          }).finally(() => {
            audioElement.value?.removeEventListener('canplay', canPlayHandler);
          });
        };
        audioElement.value.addEventListener('canplay', canPlayHandler, { once: true });
      } else {
        // 源已设置，直接播放
        audioElement.value.play().then(() => {
          isPlaying.value = true;
        }).catch((err: Error) => {
          console.error('播放失败:', err);
          isPlaying.value = false;
          
          // 触发全局事件
          if (currentMusic.value) {
            const event = new CustomEvent('playing-state-changed', { 
              detail: { 
                id: currentMusic.value.id, 
                isPlaying: false 
              } 
            });
            window.dispatchEvent(event);
          }
        });
      }
    } else {
      // 请求暂停
      audioElement.value.pause();
      isPlaying.value = false;
    }
    
    // 触发全局事件
    if (currentMusic.value) {
      const event = new CustomEvent('playing-state-changed', { 
        detail: { 
          id: currentMusic.value.id, 
          isPlaying: isPlaying.value 
        } 
      });
      window.dispatchEvent(event);
    }
  }
};
// 监听全局播放事件
onMounted(() => {
  try {
    // 创建音频元素
    audioElement.value = new Audio();
    
    // 添加音频事件监听器
    if (audioElement.value) {
      audioElement.value.addEventListener('timeupdate', handleTimeUpdate);
      audioElement.value.addEventListener('loadedmetadata', handleLoadedMetadata);
      audioElement.value.addEventListener('ended', handleEnded);
    }
    
    // 添加全局事件监听器
    window.addEventListener('play-music', handlePlayMusic as EventListener);
    window.addEventListener('toggle-play-state', handleTogglePlayState as EventListener);
  } catch (error) {
    console.error('MusicPlayer初始化失败:', error);
  }
});

// 组件卸载时清理
onUnmounted(() => {
  try {
    console.log('MusicPlayer: 组件开始卸载');
    
    // 移除全局事件监听器（先移除这些，因为它们不依赖于可能为null的audioElement）
    window.removeEventListener('play-music', handlePlayMusic as EventListener);
    window.removeEventListener('toggle-play-state', handleTogglePlayState as EventListener);
    window.removeEventListener('request-music-player-state', handleStateRequest);
    
    // 使用try/finally确保即使出错也能完成其余部分
    try {
      // 然后检查并清理音频元素
      if (audioElement.value) {
        // 防止潜在的错误中断卸载过程
        try {
          // 移除音频事件监听器
          audioElement.value.removeEventListener('timeupdate', handleTimeUpdate);
          audioElement.value.removeEventListener('loadedmetadata', handleLoadedMetadata);
          audioElement.value.removeEventListener('ended', handleEnded);
        } catch (innerError) {
          console.error('移除音频事件监听器失败:', innerError);
        }
        
        try {
          // 停止播放并清理资源
          audioElement.value.pause();
          audioElement.value.src = '';
        } catch (innerError) {
          console.error('停止音频播放失败:', innerError);
        }
        
        // 主动清除引用
        audioElement.value = null;
      }
    } finally {
      // 重置所有状态，确保干净的卸载
      showPlayer.value = false;
      isPlaying.value = false;
      currentMusic.value = null;
      progress.value = 0;
      currentTime.value = 0;
      duration.value = 0;
      playlist.value = [];
      
      console.log('MusicPlayer: 组件卸载完成');
    }
  } catch (error) {
    console.error('MusicPlayer卸载清理失败:', error);
    // 即使出错，也要确保不会阻止组件卸载
  }
});

// 关闭播放器
const closePlayer = () => {
  console.log('关闭播放器');
  
  // 先暂停播放
  if (audioElement.value) {
    audioElement.value.pause();
  }
  
  // 更新状态
  isPlaying.value = false;
  showPlayer.value = false;
  
  // 触发全局事件，通知其他组件播放状态变化
  if (currentMusic.value) {
    const event = new CustomEvent('playing-state-changed', { 
      detail: { 
        id: currentMusic.value.id, 
        isPlaying: false 
      } 
    });
    window.dispatchEvent(event);
  }
  
  // 确保通知播放器可见性变化
  notifyPlayerVisibility();
  
  // 延迟再次通知，确保所有页面收到通知
  setTimeout(() => {
    notifyPlayerVisibility();
  }, 100);
};

// 删除不再使用的变量和函数
function startProgressUpdate() {
  // 此函数不再需要，但可能被其他地方引用，所以保留空函数
}

// 监听播放状态变化
watch(isPlaying, (newValue: boolean) => {
  if (newValue) {
    // 开始播放时不需要额外操作，Audio元素会通过timeupdate事件更新进度
    if (audioElement.value && audioElement.value.paused) {
      audioElement.value.play().catch((error: Error) => {
        console.error('播放失败:', error);
        isPlaying.value = false;
      });
    }
  } else if (audioElement.value && !audioElement.value.paused) {
    // 暂停播放
    audioElement.value.pause();
  }
});
</script>

<template>
  <div 
    v-if="showPlayer && currentMusic" 
    class="music-player fixed bottom-0 left-0 right-0 lg:bottom-0 bg-gray-900/50 backdrop-blur-md text-white z-40 transition-all duration-300"
    :class="{ 'translate-y-full': !showPlayer }"
  >
    <div class="max-w-7xl mx-auto px-4 py-3 flex flex-col">
      <!-- 进度条和时间显示 -->
      <div class="flex items-center mb-2 gap-2">
        <span class="text-xs text-gray-400">{{ formattedCurrentTime }}</span>
        <div 
          class="progress-bar h-2 bg-gray-700/60 rounded-full cursor-pointer flex-1"
          @click="setProgress"
        >
          <div class="relative w-full h-full">
            <div 
              class="progress-fill h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
              :style="{ width: `${progress}%` }"
            ></div>
            <div 
              class="progress-handle absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-white shadow-md"
              :style="{ left: `calc(${progress}% - 6px)` }"
              v-show="progress > 0"
            ></div>
          </div>
        </div>
        <span class="text-xs text-gray-400">{{ formattedDuration }}</span>
      </div>
      
      <!-- 播放器主体 -->
      <div class="flex items-center justify-between">
        <!-- 左侧：歌曲信息 -->
        <div class="flex items-center flex-1 min-w-0">
          <div class="w-12 h-12 rounded-lg overflow-hidden mr-3 flex-shrink-0">
            <img 
              v-if="currentMusic?.image_url" 
              :src="currentMusic.image_url"
              class="w-full h-full object-cover"
            >
            <div v-else class="w-full h-full bg-gray-800 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
              </svg>
            </div>
          </div>
          
          <div class="min-w-0 flex-1">
            <h3 class="font-bold text-white truncate">{{ currentMusic?.title }}</h3>
            <div class="flex items-center text-sm text-gray-400">
              <span class="truncate">{{ currentMusic?.display_name }}</span>
              
            </div>
          </div>
        </div>
        
        <!-- 中间：播放控制 -->
        <div class="flex items-center space-x-4 mx-4">
          <!-- 上一首 -->
          <button 
            @click="playPrevious" 
            class="w-8 h-8 hidden lg:flex items-center justify-center text-gray-300 hover:text-white transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
          </button>
          
          <!-- 播放/暂停 -->
          <button 
            @click="togglePlay" 
            class="w-12 h-12 rounded-full flex items-center justify-center hover:opacity-90 transition-all play-button"
          >
            <svg v-if="!isPlaying" xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-white" viewBox="0 0 24 24" fill="white">
              <path d="M8 5v14l11-7z" />
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-white" viewBox="0 0 24 24" fill="white">
              <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
            </svg>
          </button>
          
          <!-- 下一首 -->
          <button 
            @click="playNext" 
            class="w-8 h-8 hidden lg:flex items-center justify-center text-gray-300 hover:text-white transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        
        <!-- 右侧：音量 -->
        <div class="flex items-center space-x-3">
          <!-- 音量控制 -->
          <div class="hidden lg:flex items-center space-x-2">
            <button 
              @click="toggleMute" 
              class="w-8 h-8 flex items-center justify-center text-gray-300 hover:text-white transition-colors"
            >
              <svg v-if="isMuted || volume === 0" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" clip-rule="evenodd" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
              </svg>
              <svg v-else-if="volume < 0.5" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" clip-rule="evenodd" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.536 8.464a5 5 0 010 7.072" />
              </svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" clip-rule="evenodd" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.536 8.464a5 5 0 010 7.072m3.536-10.608a9 9 0 010 14.144" />
              </svg>
            </button>
            
            <div 
              class="volume-bar w-20 h-1 bg-gray-700/60 rounded-full cursor-pointer hidden sm:block"
              @click="setVolume"
            >
              <div 
                class="volume-fill h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
                :style="{ width: `${volume * 100}%` }"
              ></div>
            </div>
          </div>
          
          <!-- 关闭按钮 -->
          <button 
            @click="closePlayer" 
            class="w-8 h-8 flex items-center justify-center text-gray-300 hover:text-white transition-colors ml-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.music-player {
  box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.1), 0 -2px 4px -1px rgba(0, 0, 0, 0.06);
  padding-bottom: env(safe-area-inset-bottom, 0);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px 12px 0 0;
}

.progress-bar,
.volume-bar {
  position: relative;
}

.progress-bar:hover::before,
.volume-bar:hover::before {
  content: '';
  position: absolute;
  top: -4px;
  left: 0;
  right: 0;
  bottom: -4px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 9999px;
}

.play-button {
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  box-shadow: 0 4px 6px -1px rgba(59, 130, 246, 0.3), 0 2px 4px -1px rgba(139, 92, 246, 0.2);
}

.progress-handle {
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
  transition: transform 0.1s ease;
}

.progress-handle:hover {
  transform: translate(0, -50%) scale(1.2);
}
</style> 